import { useQuery } from "@tanstack/react-query";
import { File, FileText, Image as ImageIcon } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { promptPreviewApi, type PreviewContentBlockDto } from "@/api/reportsV32";
import { TokenBudgetDisplay } from "./TokenBudgetDisplay";

// LLM & Reports v3.2 §6.7 / §8.1 step 5 — "Preview what's being sent
// to Claude." Local assembly preview only; no Anthropic call.
//
// Renders three panels:
//   1. Resolved prompt body (what Claude reads after placeholder
//      substitution and branding-guide prepend),
//   2. The list of content blocks Claude will see — text, document
//      (PDF), image — with per-block token estimates,
//   3. The aggregate token budget vs threshold via the shared
//      TokenBudgetDisplay component.

export interface PreviewPromptModalProps {
  open: boolean;
  onClose: () => void;
  submissionId: string | null;
}

function blockIcon(kind: PreviewContentBlockDto["kind"]) {
  if (kind === "image") return <ImageIcon size={16} aria-hidden />;
  if (kind === "document") return <File size={16} aria-hidden />;
  return <FileText size={16} aria-hidden />;
}

export function PreviewPromptModal({ open, onClose, submissionId }: PreviewPromptModalProps) {
  const preview = useQuery({
    queryKey: ["preview-prompt", submissionId],
    queryFn: () => promptPreviewApi.preview(submissionId!),
    enabled: open && submissionId !== null,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Preview what's being sent to Claude"
      description="Local assembly preview. Nothing has been sent to Anthropic yet."
      size="lg"
    >
      {preview.isLoading && (
        <p className="font-body text-body text-gray-mid">Building preview…</p>
      )}
      {preview.isError && (
        <p className="font-body text-body text-status-error">
          Could not load preview. Try again, or open the report's submission again.
        </p>
      )}
      {preview.data && (
        <div className="space-y-space-4">
          <TokenBudgetDisplay estimate={preview.data.tokenBudget} />

          <section>
            <h3 className="font-display text-h4 text-onyx mb-space-2">Content blocks</h3>
            <ul className="divide-y divide-border-light rounded-md border border-border-light">
              {preview.data.contentBlocks.map((block, i) => (
                <li
                  key={`${block.kind}-${block.title}-${i}`}
                  className="flex items-center justify-between gap-space-3 px-space-3 py-space-2"
                >
                  <span className="flex items-center gap-space-2 min-w-0">
                    <span className="text-gold-shadow shrink-0">{blockIcon(block.kind)}</span>
                    <span className="font-body text-body text-onyx truncate">{block.title}</span>
                    {block.mediaType && (
                      <span className="font-body text-caption text-gray-mid shrink-0">
                        {block.mediaType}
                      </span>
                    )}
                  </span>
                  <span className="font-body text-caption text-gray-mid whitespace-nowrap">
                    ~{block.estimatedTokens.toLocaleString()} tokens
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h3 className="font-display text-h4 text-onyx mb-space-2">Resolved prompt</h3>
            <pre className="bg-white-warm rounded-md border border-border-light p-space-3 text-caption text-onyx whitespace-pre-wrap font-mono max-h-80 overflow-y-auto">
              {preview.data.promptBody || "(empty)"}
            </pre>
          </section>
        </div>
      )}
    </Modal>
  );
}
